import React, { ElementType } from 'react';
import * as CSS from 'csstype';
import styled, { css } from 'styled-components';
import { colors, typography } from '../../settings';

export interface TextProps {
    align?: CSS.Property.TextAlign;
    as?: ElementType<React.HTMLAttributes<HTMLElement>>;
    children: React.ReactNode;
    color?: CSS.Property.Color | colors.Color;
    /**
     * Sets the font-family CSS property based on typography settings
     */
    fontFamily?: keyof typeof typography.fontFamilies;
    italic?: boolean;
    /**
     * Sets the line-height CSS property based on typography settings.
     */
    lineHeight?: keyof typeof typography.lineHeights;
    /**
     * Sets the font-size CSS property based on typography settings
     */
    size?: keyof typeof typography.typeScale;
    /**
     * Truncates the text with an ellipsis if the content exceeds it's
     * container's width. Text must be also be a block element.
     */
    truncate?: boolean;
    /**
     * Sets the overflow-wrap CSS property
     */
    overflowWrap?: CSS.Property.OverflowWrap;
    /**
     * Sets the word-break CSS property
     */
    wordBreak?: CSS.Property.WordBreak;
    /**
     * Sets the font-weight CSS property based on typography settings
     */
    weight?: keyof typeof typography.fontWeights;
}

export function textStyles(props: TextProps) {
    const {
        align,
        color = 'puntRoad',
        fontFamily = 'root',
        italic,
        lineHeight,
        size = 'size3',
        truncate,
        overflowWrap,
        wordBreak,
        weight = 'root',
    } = props;

    return css`
        /* Removes the global margin applied to <p> */
        margin-bottom: 0;

        ${align &&
        css`
            text-align: ${align};
        `}
        ${italic &&
        css`
            font-style: italic;
        `}
        ${truncate &&
        css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
        `}
        ${lineHeight &&
        css`
            line-height: ${typography.lineHeights[lineHeight]};
        `}
        ${overflowWrap &&
        css`
            overflow-wrap: ${overflowWrap};
        `}
        ${wordBreak &&
        css`
            word-break: ${wordBreak};
        `}

        color: ${colors.getColor(color)};
        font-family: ${typography.fontFamilies[fontFamily]};
        font-size: ${typography.typeScale[size]}px;
        font-weight: ${typography.fontWeights[weight]};
    `;
}

export const Text = styled.span.withConfig({
    shouldForwardProp: (prop, defaultValidatorFn) =>
        !['color', 'fontFamily'].includes(prop) && defaultValidatorFn(prop),
})(textStyles);
